import React from 'react';
import { formatRecommendation } from '../../utils/formatRecommendation';

/**
 * Renders AI Insights section with proper Alternative Path formatting
 * DRY: Used by both DecisionRenderer and TestCaseComparison
 */
const AIInsightsSection = ({ 
  aiRecommendation,
  synthesis = null,
  errorIndicators = null
}) => {
  const contentToRender = synthesis || aiRecommendation;
  
  if (!contentToRender) {
    return (
      <div className="glass-card p-6 mb-8" style={{ background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1), rgba(168, 85, 247, 0.15))' }}>
        <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          AI Insights
        </h3>
        <p className="text-gray-500 italic">AI analysis will appear here...</p>
      </div>
    );
  }

  return (
    <div className="glass-card p-6 mb-8" style={{ background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1), rgba(168, 85, 247, 0.15))' }}>
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          AI Insights
        </h3>
        {errorIndicators}
      </div>
      
      <div className="text-lg text-gray-700 leading-relaxed space-y-3" data-testid="ai-recommendation">
        {formatRecommendation(contentToRender).map((item) => {
          // Handle Option C box specially
          if (item.type === 'option-c-box') {
            const cleanContent = item.content.replace(/[┌┐├┤└┘─│]/g, '').trim();
            const lines = cleanContent.split('\n').filter(line => line.trim());
            
            let optionCName = '';
            let whatItIs = '';
            let whyItems = [];
            let solutionType = '';
            let closingNote = '';
            
            let currentSection = '';
            lines.forEach(line => {
              const trimmed = line.trim();
              if (trimmed.includes('Alternative Path Detected')) return;
              if (trimmed.startsWith('OPTION C:')) {
                optionCName = trimmed.replace('OPTION C:', '').trim();
                currentSection = 'name';
              } else if (trimmed === 'What it is:') {
                currentSection = 'what';
              } else if (trimmed === 'Why this might work:') {
                currentSection = 'why';
              } else if (trimmed.includes('This is a')) {
                solutionType = trimmed;
                currentSection = 'type';
              } else if (trimmed.includes('Does that feel')) {
                closingNote = trimmed;
              } else if (currentSection === 'what' && trimmed) {
                whatItIs += (whatItIs ? ' ' : '') + trimmed;
              } else if (currentSection === 'why' && trimmed.startsWith('•')) {
                whyItems.push(trimmed.substring(1).trim());
              } else if (currentSection === 'type' && trimmed) {
                closingNote = trimmed;
              }
            });
            
            return (
              <div key={item.key} className="glass-card p-6 sm:p-8 my-6 border-l-4 border-purple-500">
                <div className="flex items-start gap-3 mb-6">
                  <span className="text-3xl flex-shrink-0">💡</span>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-purple-900 mb-1">Alternative Path Detected</h3>
                    <p className="text-purple-700 text-sm sm:text-base">A third way forward</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  {optionCName && (
                    <div>
                      <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        {optionCName.replace(/\\*\\*/g, '')}
                      </h4>
                    </div>
                  )}
                  
                  {whatItIs && (
                    <div className="bg-white/50 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">What it is:</h5>
                      <p className="text-gray-700 leading-relaxed">
                        {whatItIs.split('**').map((part, idx) => 
                          idx % 2 === 1 ? <strong key={idx} className="font-bold text-gray-900">{part}</strong> : part
                        )}
                      </p>
                    </div>
                  )}
                  
                  {whyItems.length > 0 && (
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-3">Why this might work:</h5>
                      <ul className="space-y-2">
                        {whyItems.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-purple-500 mt-1 flex-shrink-0">✓</span>
                            <span className="text-gray-700 leading-relaxed">
                              {item.split('**').map((part, pIdx) => 
                                pIdx % 2 === 1 ? <strong key={pIdx} className="font-bold text-gray-900">{part}</strong> : part
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {solutionType && (
                    <div className="pt-4 border-t border-purple-200">
                      <p className="text-gray-700">
                        {solutionType.split('**').map((part, idx) => 
                          idx % 2 === 1 ? <strong key={idx} className="font-bold text-purple-900">{part}</strong> : part
                        )}
                      </p>
                    </div>
                  )}
                  
                  {closingNote && !closingNote.includes('Does that feel') && (
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-gray-700 italic leading-relaxed">{closingNote}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          }
          
          // Regular paragraph
          return (
            <p key={item.key}>
              {item.parts.map((part, idx) => 
                part.type === 'bold' ? (
                  <strong key={idx} className="font-bold text-gray-900">{part.content}</strong>
                ) : (
                  <span key={idx}>{part.content}</span>
                )
              )}
            </p>
          );
        })}
      </div>
    </div>
  );
};

export default AIInsightsSection;
